import {  Root, Handle, Dropdown, DropdownTitle  } from 'file:///root/project/node_modules/.cache/react-style-loader/expressions_FnExpression-ACDY.styl';
import React, { PureComponent } from 'react';
import cx from 'classnames';
import _ from 'lodash';
import { connect } from 'react-redux';

import { getMethod, Method, MethodArg } from '../utils/methods';
import Select from '../components/Select';
import TextArea from '../components/TextArea/TextArea';
import TextInput from '../components/TextInput';
import { GlobalResourceDrawerHandle } from 'src/components/ResourceDrawer';
import { Switch } from '@blueprintjs/core';
import type { ExpressionComponent } from '../utils/utils';
import type { FormulaNode, FormulaOption, UiFormulaNode, UiLog } from '../typeDefs';
import type { RootState } from '../redux/store';

type ArgConfig = { formulaOptions: FormulaOption[]; data: UiFormulaNode };
type ArgItem = MethodArg | ((config: ArgConfig) => MethodArg | null);

interface FnExpressionProps {
    data: UiFormulaNode;
    formulaOptions: FormulaOption[];
}

class FnExpression extends PureComponent<FnExpressionProps> {
    static getStub = ([type = 'perServing']: string[] = []): FormulaNode => FnExpression.getStubFor(type);

    static getStubFor = (name: string): FormulaNode => ({
        $op: 'fn',
        $value: name,
        $args: _.cloneDeep(getMethod(name)!.args).map((arg) =>
            typeof arg === 'function' || arg.type !== 'match' ? null : arg.list.fill(null)),
    });

    renderArg = (rawItem: ArgItem, index: number): React.ReactNode => {
        const item = typeof rawItem === 'function' ? rawItem(this.props) : rawItem;
        if (!item) return null;

        const { prefix, name, type, label, description, options, list, allowInput, ...rest } = item;
        const { data: { ID, change, $value, $args, state } } = this.props;
        const key = (prefix ? [prefix, index] : [index]).join('.');

        const _onChange = (value: unknown) => {
            if (_.get($args, key) === value) return;
            return change('$args.' + key, value);
        };

        const onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => _onChange(event.target.value);

        // interactive elements - like dropdowns - need to stay visible between changes
        const keyWithoutArgValue = `${ID}-${$value}-${prefix}-${index}`;

        const props = {
            ...rest,
            key: `${ID}-${$value}-${_.get($args, key)}-${prefix}-${index}`,
            name,
            title: description,
            className: `expression-fn-${type}`,
        };
        const normalProps = {
            ...props,
            readOnly: state.readOnly,
            value: _.get($args, key),
            onChange,
        };
        const inputProps = {
            ...props,
            autoComplete: 'off',
            readOnly: state.readOnly,
            defaultValue: _.get($args, key),
            onBlur: onChange,
        };

        const getExtendedValue = (arg: MethodArg): unknown => {
            const { data } = this.props;
            const { key: argKey, value } = arg;
            const extra: string[] = _.get(data, `rootRef.varTypes[${argKey}]`, []);
            if (!extra.length) return value;
            return [
                ...extra.map((varName: string) => [varName, 'args.' + varName, 'Variables']),
                ...value,
            ];
        };

        switch (type) {
            // new
            case 'enum': return <Select {...normalProps} options={getExtendedValue(item) as ReadonlyArray<unknown>[]} />;
            case 'boolean':
                return <Switch
                    {...normalProps}
                    value={undefined}
                    checked={normalProps.value}
                    onChange={(e: React.FormEvent<HTMLInputElement>) => _onChange(e.currentTarget.checked)}
                    label={label}
                    className='mb0 ml05'
                />;
            // old
            case 'select':
                return <Select
                    {...normalProps}
                    options={item.getOptions ? item.getOptions(this.props) : options}
                />;
            case 'text':
            case 'smalltext':
                return <TextInput
                    placeholder={label}
                    minWidth={60}
                    maxWidth={400}
                    {...inputProps}
                    className={cx(inputProps.className, type === 'smalltext' && 'small')}
                    onBlur={undefined}
                    onChange={_onChange}
                />;
            case 'ref': {
                const showTextField = allowInput && !Array.isArray(normalProps.value);
                const handle = <GlobalResourceDrawerHandle
                    key={keyWithoutArgValue}
                    readOnly={normalProps.readOnly}
                    emptyLabel={allowInput ? undefined : '[Select List]'}
                    allowEmptyValue={!!allowInput}
                    value={showTextField ? [] : normalProps.value || []}
                    onChange={(val: unknown) => _onChange(_.isEmpty(val) ? null : val)}
                />;

                if (!showTextField) return handle;

                return <React.Fragment key={keyWithoutArgValue}>
                    {handle}
                    <TextInput
                        minWidth={60}
                        maxWidth={400}
                        placeholder={label}
                        {...inputProps}
                        className='text'
                        onChange={(value: string) => _onChange(value ? value.trim() : null)}
                    />
                </React.Fragment>;
            }
            case 'textarea':
                return <TextArea
                    placeholder={label}
                    cols={35}
                    {...props}
                    readOnly={state.readOnly}
                    defaultValue={inputProps.defaultValue}
                    onChange={(val) => _onChange(val)}
                />;
            case 'match':
                return <div {...props}>
                    {list.map((arg: MethodArg, subIndex: number) =>
                        <label key={subIndex}>
                            {arg.label}:
                            {this.renderArg({
                                ...arg,
                                prefix: (prefix || '') + index,
                            }, subIndex)}
                        </label>,
                    )}
                </div>;
        }

        throw new Error(`Invalid arg type: '${type}'`);
    };

    render() {
        const { data: { $value, log } } = this.props;
        const fnData = getMethod($value);

        if (!fnData) throw new Error(`Invalid Function name: '${$value}'`);

        const formatArgs = fnData.formatArgs || ((args: React.ReactNode[]) => args);

        return <div className='expression-fn'>
            {formatArgs(fnData.args.map((item, index) => this.renderArg(item, index)))}
            {fnData.showInlineArtifacts && log && <InlineArtifacts log={log} />}
        </div>;
    }
}

export default connect((state: RootState) => ({
    formulaOptions: state.formula && state.formula.data && state.formula.data.options,
}))(FnExpression) as unknown as ExpressionComponent;

interface InlineArtifactsProps {
    log: UiLog;
}

interface InlineArtifactsState {
    expanded: boolean;
}

class InlineArtifacts extends PureComponent<InlineArtifactsProps, InlineArtifactsState> {
    state: InlineArtifactsState = { expanded: false };

    renderDropdown(handle: React.ReactNode, renderContent: () => React.ReactNode = (() => handle)) {
        return <div
            onMouseEnter={() => this.setState({ expanded: true })}
            onMouseLeave={() => this.setState({ expanded: false })}
        >
            <Handle>{handle}</Handle>
            {this.state.expanded && <Dropdown>{renderContent()}</Dropdown>}
        </div>;
    }

    render() {
        const { log, ...rest } = this.props;

        if (!log || !log.hasLog) return null;

        const value = _.map(log.artifacts as unknown[], '$value')[0];

        return (
            <Root {...rest}>
                {Array.isArray(value)
                    ? this.renderDropdown(
                        (!value[0] || typeof value[0] === 'string')
                            ? `list[${value.length}] = ${value.join('; ')}`
                            : `list[${value.length}] = ${JSON.stringify(value)}`,
                        () => <React.Fragment>
                            <DropdownTitle>{value.length} items</DropdownTitle>
                            {value.map((x: unknown, i: number) =>
                                <pre key={i} style={{ margin: 0 }}>
                                    {x != null && typeof x === 'object'
                                        ? JSON.stringify(x, null, 4)
                                        : (x as React.ReactNode)
                                    }
                                </pre>,
                            )}
                        </React.Fragment>,
                    )
                    : this.renderDropdown(
                        JSON.stringify(value),
                        () => <pre style={{ fontSize: '.75rem' }}>{JSON.stringify(value, null, 4)}</pre>,
                    )
                }
            </Root>
        );
    }
}

